import React from 'react';
import { useEntity } from "@backstage/plugin-catalog-react";
import Table from "@mui/material/Table/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { InfoCard, ItemCardHeader } from "@backstage/core-components";
import { Entity } from "@backstage/catalog-model";

type EntitlementRuleValue = {
    compliant?: string;
    comments?: string;
};

type EntitlementRuleRow = {
    rule: string;
    ruleDisplayName: string;
    compliant: string;
    comment: string;
};

const toSentenceCaseFromKey = (value: string) => {
    const withSpaces = value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();

    if (!withSpaces) return value;

    return withSpaces
        .toLowerCase()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatDisplayValue = (value: string) => {
    if (value?.trim().toLowerCase() === 'n/a') {
        return 'N/A';
    }

    return toSentenceCaseFromKey(value);
};

const isTruthyAnnotationValue = (value?: string) => {
    if (!value) return false;
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes';
};

const isFalseyAnnotationValue = (value?: string) => {
    if (!value) return false;
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'no';
};

const normalizeTrueFalse = (value?: string) => {
    if(isTruthyAnnotationValue(value)) return 'True';
    else if (isFalseyAnnotationValue(value)) return 'False';
    return value;
};

//Entitlement Rules Table with same header background as API Details.
const EntitlementRulesTable = ({
    entitlementRules,
    enforcesEntitlements,
}: {
    entitlementRules: EntitlementRuleRow[];
    enforcesEntitlements: boolean;
}) => {
    const hasEntitlementContent = entitlementRules.some(rule => {
        const compliant = rule.compliant?.trim().toLowerCase();
        const hasCompliant = compliant && compliant !== 'n/a';
        return !!(hasCompliant );
    });
    if (!hasEntitlementContent&&enforcesEntitlements) return (
        <InfoCard title="Entitlement Integration Rules">
            <div style={{
                width: '100%',
                padding: '16px',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '1rem',
                color: 'var(--mui-palette-text-primary, var(--color-text-primary, inherit))',
            }}>
                Entitlement is enabled for this service, but is yet to be updated in the catalog.
            </div>

        </InfoCard>
    );
    return (
        <InfoCard title="Entitlement Integration Rules">
            {enforcesEntitlements ? (
                <div style={{ width: '100%', border: '1px solid var(--mui-palette-divider, var(--color-divider, #e0e0e0))', overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow style={{borderBottom: '1px solid var(--mui-palette-divider, var(--color-divider, #e0e0e0))'}}>
                                <TableCell style={{ width: '35%', fontWeight: 750, color:'black' ,fontSize: '1rem'}}>Rule</TableCell>
                                <TableCell style={{ width: '20%', fontWeight: 750, color:'black', fontSize: '1rem' }}>Compliant</TableCell>
                                <TableCell style={{ width: '45%', fontWeight: 750, color:'black', fontSize: '1rem' }}>Comment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entitlementRules.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell component="th" scope="row" style={{ width: '35%', fontWeight: 600 }}>
                                        {row.ruleDisplayName}
                                    </TableCell>
                                    <TableCell style={{ width: '20%' }}>{formatDisplayValue(row.compliant) || ''}</TableCell>
                                    <TableCell style={{ width: '45%' }}>{formatDisplayValue(row.comment) || ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) :
                <div style={{
                    width: '100%',
                    padding: '16px',
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: 'var(--mui-palette-text-primary, var(--color-text-primary, inherit))',
                }}>
                    Entitlement is not enforced for this service.
                </div>}
        </InfoCard>
    );
}

const CustomEirTable = () => {
    const { entity } = useEntity<Entity>();
    const enforcesEntitlements = isTruthyAnnotationValue(
        entity.metadata.annotations?.['backstage.io/enforcesEntitlements'],
    );

    const entitlementRuleKeys = [
        "assetConditionEnforcement",
        "clientTypeAgnosticCompliance",
        "commonLibraryStandardization",
        "dynamicRuleExternalization",
        "corpExperienceFlagResilience",
        "entitlementsContractCompliance",
        "filterConditionEnforcement",
        "newAttributeTolerance",
        "sessionCacheKeyStandard",
        "standardInternalAccessControlImplementation",
        "tokenDecodingRestriction",
    ];

    const specEntitlementRules = (entity.spec as { entitlementIntegrationRules?: Record<string, EntitlementRuleValue> } | undefined)
        ?.entitlementIntegrationRules ?? {};

    const entitlementRules: EntitlementRuleRow[] = entitlementRuleKeys.map(ruleKey => {
        const ruleFromSpec = specEntitlementRules?.[ruleKey];
        const legacyCompliant = entity.metadata.annotations?.[`backstage.io/entitlementIntegrationRules/${ruleKey}`];
        const compliant = ruleFromSpec?.compliant ?? legacyCompliant ?? 'N/A';
        const comment = ruleFromSpec?.comments ?? '';

        return {    
            rule: ruleKey,
            ruleDisplayName: toSentenceCaseFromKey(ruleKey),
            compliant: normalizeTrueFalse(String(compliant)),
            comment: comment ? String(comment) : '',
        };
    });
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <EntitlementRulesTable entitlementRules={entitlementRules} enforcesEntitlements={enforcesEntitlements} />
        </div>
    );
}



export default CustomEirTable;
